import { useSnackbar } from 'notistack';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BooleanParam, useQueryParam } from 'use-query-params';
import useDecisionTreePreviewContext from '../../Contexts/DecisionTree/DecisionTreePreviewContext';
import useAudioDeviceConfig from '../../Hooks/audio/useAudioDeviceConfig';

export const AUDIO_WARNING_SNACKBAR_KEY = 'audio-warning';

export function AudioWarningMessage({ microphoneAuthorized }: { microphoneAuthorized: boolean }) {
  const { t } = useTranslation('microphone');
  return (
    <span>
      {microphoneAuthorized ? t('no-device') : t('authorize')}
    </span>
  );
}

export default function AudioWarning() {
  const config = useAudioDeviceConfig();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [isCardPreview] = useQueryParam('preview', BooleanParam);
  const isDecisionTreePreview = useDecisionTreePreviewContext();
  const isSnackbarOpen = React.useRef(false);
  const microphoneAuthorized = React.useMemo(() => config.dual || config.mono?.config.permissionStatus === 'granted', [config.dual, config.mono?.config.permissionStatus]);
  const noDevice = React.useMemo(
    () => {
      if (config.dual) {
        return !config.dual.input?.selected || !config.dual.output?.selected;
      }
      if (config.mono?.config) {
        return !config.mono.config.deviceId;
      }
      return true;
    },
    [config.dual, config.mono?.config.deviceId, config.mono?.config],
  );
  const shouldShowWarning = React.useMemo(() => !isCardPreview && !isDecisionTreePreview && (!microphoneAuthorized || noDevice), [isCardPreview, isDecisionTreePreview, microphoneAuthorized, noDevice]);

  React.useEffect(() => {
    if (shouldShowWarning && !isSnackbarOpen.current) {
      enqueueSnackbar(<AudioWarningMessage microphoneAuthorized={!!microphoneAuthorized} />, {
        key: AUDIO_WARNING_SNACKBAR_KEY,
        persist: true,
        variant: 'error',
        action: null,
      });

      isSnackbarOpen.current = true;
    }

    if (!shouldShowWarning && isSnackbarOpen.current) {
      closeSnackbar(AUDIO_WARNING_SNACKBAR_KEY);
      isSnackbarOpen.current = false;
    }
  }, [shouldShowWarning, enqueueSnackbar, closeSnackbar, microphoneAuthorized]);

  return null;
}
